import * as React from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEventById,
  getEventAttendance,
  updateParticipantStatus,
  bulkUpdateParticipantStatus,
  addParticipant,
  bulkAddParticipants,
  getEventChecklist,
  updateChecklistItem,
} from "@/lib/queries/events";
import { getEventFeedbackSummary } from "@/lib/queries/feedback";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  ArrowRight,
  Clock,
  DollarSign,
  Calendar,
  Clock4,
  MapPinned,
  CheckCircle2,
  BarChart,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Users,
  User,
  MessageSquare,
  HelpCircle,
  FileText,
  MapPin,
  SendHorizonal,
  Upload,
  X,
  Link,
  ExternalLink,
  Building,
  Image,
  Info,
  Check,
  Star,
  Heart,
  Loader2,
  Play,
  Video,
  FileImage,
  Download,
  Package,
  Copy,
  Banknote,
  CheckSquare,
  ListChecks,
  Filter,
  ChevronDown,
  Circle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { LoadingDialog } from "@/components/ui/loading-dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectGroup,
  SelectLabel,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import {
  format,
  parseISO,
  isAfter,
  isBefore,
  isSameDay,
  differenceInDays,
} from "date-fns";
import Papa from "papaparse";
import { ParticipantTable } from "./components/ParticipantTable";
import { ParticipantStats } from "./components/ParticipantStats";
import { ParticipantFilters } from "./components/ParticipantFilters";

export default function EventDetails({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) {
  // Helper function to format event status for display
  const formatStatus = (status: string): string => {
    if (!status) return "Unknown";

    // Replace underscores with spaces and capitalize each word
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to get status color for UI elements
  const getStatusColor = (status: string): string => {
    switch (status) {
      case "event_draft":
        return "bg-gray-100 text-gray-800";
      case "event_pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "event_confirmed":
        return "bg-green-100 text-green-800";
      case "event_in_progress":
        return "bg-blue-100 text-blue-800";
      case "event_complete":
        return "bg-purple-100 text-purple-800";
      case "event_cancelled":
        return "bg-red-100 text-red-800";
      case "payment_pending":
        return "bg-orange-100 text-orange-800";
      case "payment_failed":
        return "bg-red-200 text-red-900";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to check if user can interact with checklist item
  const canUserInteractWithItem = (itemOwner: string): boolean => {
    if (!profile?.user_type) return false;

    switch (profile.user_type) {
      case "nonprofit":
        return itemOwner === "npo" || itemOwner === "cc";
      case "company":
        return itemOwner === "corporate" || itemOwner === "cc";
      case "host":
        return itemOwner === "host";
      default:
        return false;
    }
  };

  // Helper function to check if owner badge should be shown
  const shouldShowOwnerBadge = (itemOwner: string): boolean => {
    if (!profile?.user_type) return true;

    switch (profile.user_type) {
      case "nonprofit":
        // Don't show NPO badge for nonprofit users, but show others
        return itemOwner !== "npo";
      case "company":
        // Don't show Corporate badge for corporate users, but show others
        return itemOwner !== "corporate" && itemOwner !== "cc";
      case "host":
        // Show all badges for host users since they need to see who owns what
        return true;
      default:
        return true;
    }
  };

  const { id } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [showStartDialog, setShowStartDialog] = React.useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = React.useState(false);
  const [startConfirmText, setStartConfirmText] = React.useState("");
  const [cancelReason, setCancelReason] = React.useState("");

  const {
    data: eventResponse,
    isLoading: isEventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", id],
    queryFn: () => getEventById(id!, session?.access_token!),
    enabled: !!id && !!session?.access_token,
  });

  const {
    data: attendanceData,
    isLoading: isLoadingAttendance,
    refetch: refetchAttendance,
  } = useQuery({
    queryKey: ["eventAttendance", id],
    queryFn: () => getEventAttendance(id!, session?.access_token),
    enabled: !!id && !!session?.access_token,
  });

  // Fetch event feedback summary
  const {
    data: feedbackSummary,
    isLoading: isFeedbackLoading,
    error: feedbackError,
  } = useQuery({
    queryKey: ["event-feedback-summary", id],
    queryFn: () => getEventFeedbackSummary(id!, session?.access_token!),
    enabled: !!id && !!session?.access_token,
  });

  // Fetch event checklist
  const {
    data: checklistData,
    isLoading: isChecklistLoading,
    error: checklistError,
    refetch: refetchChecklist,
  } = useQuery({
    queryKey: ["event-checklist", id],
    queryFn: () => getEventChecklist(id!, session?.access_token),
    enabled: !!id && !!session?.access_token,
  });

  // Checklist filter and sort states
  const [checklistOwnerFilter, setChecklistOwnerFilter] =
    React.useState<string>("all");
  const [checklistSortBy, setChecklistSortBy] = React.useState<
    "timeline" | "status"
  >("timeline");
  const [checklistEventTypeFilter, setChecklistEventTypeFilter] =
    React.useState<string>("all");

  // Compute summary data from items
  const checklistSummary = React.useMemo(() => {
    const items = checklistData?.checklist?.items || [];
    const total = items.length;
    const completed = items.filter((item) => item.completed).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Calculate by owner
    const byOwner = {
      npo: { total: 0, completed: 0 },
      corporate: { total: 0, completed: 0 },
      host: { total: 0, completed: 0 },
      cc: { total: 0, completed: 0 },
    };

    items.forEach((item) => {
      const owner = item.owner as keyof typeof byOwner;
      if (byOwner[owner]) {
        byOwner[owner].total++;
        if (item.completed) {
          byOwner[owner].completed++;
        }
      }
    });

    return {
      total,
      completed,
      percentage,
      byOwner,
    };
  }, [checklistData?.checklist?.items]);

  const updateInvite = useMutation({
    mutationFn: async ({
      inviteId,
      status,
    }: {
      inviteId: string;
      status: string;
    }) => {
      setIsLoading(true);

      const apiUrl = import.meta.env.VITE_CC_API_URL || "";

      const response = await fetch(`${apiUrl}/v1/hooks/event-lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: status === "accepted" ? "npo_accepted" : "npo_rejected",
          eventId: id,
          inviteId: inviteId,
          remarks: `NPO ${status} the event`,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update event lifecycle");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      toast({
        title: "Success",
        description: "Invite response submitted successfully",
      });
      setIsLoading(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update invite status",
        variant: "destructive",
      });
      setIsLoading(false);
    },
  });

  const handleInviteResponse = (inviteId: string, status: string) => {
    if (eventResponse?.id) {
      updateInvite.mutate({ inviteId, status });
    }
  };

  // Mutation for cancelling an event
  const cancelEvent = useMutation({
    mutationFn: async (reason: string) => {
      setIsLoading(true);

      const apiUrl = import.meta.env.VITE_CC_API_URL || "";

      const response = await fetch(`${apiUrl}/v1/hooks/event-lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: "event_cancelled",
          eventId: id,
          remarks: reason || "Event cancelled by NPO",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to cancel event");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      toast({
        title: "Success",
        description: "Event has been cancelled successfully",
      });
      setIsLoading(false);
      setShowCancelDialog(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to cancel event",
        variant: "destructive",
      });
      setIsLoading(false);
    },
  });

  // Mutation for starting an event
  const startEvent = useMutation({
    mutationFn: async () => {
      setIsLoading(true);

      const apiUrl = import.meta.env.VITE_CC_API_URL || "";

      const response = await fetch(`${apiUrl}/v1/hooks/event-lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: "event_in_progress",
          eventId: id,
          remarks: "Event started by NPO",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to start event");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      toast({
        title: "Success",
        description: "Event has been started successfully",
      });
      setIsLoading(false);
      setShowStartDialog(false);
      setStartConfirmText("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to start event",
        variant: "destructive",
      });
      setIsLoading(false);
    },
  });

  // Mutation for completing an event
  const completeEvent = useMutation({
    mutationFn: async () => {
      setIsLoading(true);

      const apiUrl = import.meta.env.VITE_CC_API_URL || "";

      const response = await fetch(`${apiUrl}/v1/hooks/event-lifecycle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: "event_complete",
          eventId: id,
          remarks: "Event completed by NPO",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to complete event");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", id] });
      toast({
        title: "Success",
        description: "Event has been marked as completed",
      });
      setIsLoading(false);
      setShowCompleteDialog(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to complete event",
        variant: "destructive",
      });
      setIsLoading(false);
    },
  });

  const handleCancelEvent = () => {
    cancelEvent.mutate(cancelReason);
  };

  const handleStartEvent = () => {
    if (
      differenceInDays(eventDate, today) !== 0 &&
      startConfirmText !== "i agree to start the event"
    ) {
      toast({
        title: "Error",
        description: "Please type the confirmation text exactly as shown",
        variant: "destructive",
      });
      return;
    }

    startEvent.mutate();
  };

  const handleCompleteEvent = () => {
    completeEvent.mutate();
  };

  // Checklist update mutation
  const updateChecklistItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      completed,
    }: {
      itemId: string;
      completed: boolean;
    }) => {
      return updateChecklistItem(id!, itemId, completed, session?.access_token);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Checklist item updated successfully",
      });
      refetchChecklist();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update checklist item",
        variant: "destructive",
      });
    },
  });

  const handleChecklistItemToggle = (
    itemId: string,
    currentStatus: boolean
  ) => {
    setUpdatingItems((prev) => ({ ...prev, [itemId]: true }));
    updateChecklistItemMutation.mutate(
      {
        itemId,
        completed: !currentStatus,
      },
      {
        onSettled: () => {
          setUpdatingItems((prev) => {
            const newState = { ...prev };
            delete newState[itemId];
            return newState;
          });
        },
      }
    );
  };

  // Event updates state
  const [showUpdateForm, setShowUpdateForm] = React.useState(false);
  const [updateType, setUpdateType] = React.useState("");
  const [updateContent, setUpdateContent] = React.useState("");
  const [attachmentUrl, setAttachmentUrl] = React.useState("");
  const [sendToBooker, setSendToBooker] = React.useState(false);
  const [sendToParticipants, setSendToParticipants] = React.useState(false);
  const [isSubmittingUpdate, setIsSubmittingUpdate] = React.useState(false);
  const [mockUpdates, setMockUpdates] = React.useState<any[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const [updatingItems, setUpdatingItems] = React.useState<
    Record<string, boolean>
  >({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const contentTextareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Determine if the update type should show an upload button
  const shouldShowUploadButton = () => {
    return (
      true ||
      [
        "EVENT_UPDATE",
        "EVENT_PHOTOS",
        "EVENT_BROCHURES",
        "EVENT_COLLATERALS",
        "POST_EVENT_IMPACT",
        "ATTENDANCE",
      ].includes(updateType)
    );
  };

  // Handle file upload to Supabase
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      !e.target.files ||
      e.target.files.length === 0 ||
      !session?.access_token
    ) {
      return;
    }

    const selectedFile = e.target.files[0];
    setIsUploading(true);

    try {
      // Get user ID from session
      const userId = session.user.id;

      // Upload to Supabase storage
      const fileExt = selectedFile.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${userId}/events/${id}/attachments/${fileName}`;

      await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });

      // Upload file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from("nonprofit-cloud")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Get the public URL of the uploaded file
      const {
        data: { publicUrl },
      } = supabase.storage.from("nonprofit-cloud").getPublicUrl(filePath);

      // Set the attachment URL to the public URL
      setAttachmentUrl(publicUrl);

      toast({
        title: "File uploaded successfully",
        description: "Your file has been uploaded and attached to this update.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload failed",
        description:
          "There was an error uploading your file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle update type change to set defaults
  const handleUpdateTypeChange = (newType: string) => {
    setUpdateType(newType);

    // Set default email settings based on type
    switch (newType) {
      case "EVENT_PHOTOS":
      case "EVENT_BROCHURES":
      case "EVENT_LOCATION":
      case "EVENT_MEET_LINK":
      case "EVENT_UPDATE":
      case "THANKYOU":
      case "VOLUNTEER_SUBMISSION_LINK":
        // Send to both booker and participants
        setSendToBooker(true);
        setSendToParticipants(false);
        break;
      case "EVENT_HOST_DETAILS":
      case "EVENT_COLLATERALS":
      case "EVENT_SHIPMENTS":
      case "POST_EVENT_IMPACT":
      case "ATTENDANCE":
        // Send only to booker
        setSendToBooker(true);
        setSendToParticipants(false);
        break;
      default:
        // For other types, don't set defaults
        setSendToBooker(true);
        setSendToParticipants(false);
    }
  };

  // Get field label based on update type
  const getContentFieldLabel = () => {
    switch (updateType) {
      case "EVENT_PHOTOS":
        return "Photo Drive Link";
      case "EVENT_BROCHURES":
        return "Brochure Description";
      case "EVENT_LOCATION":
        return "New Location Details";
      case "EVENT_MEET_LINK":
        return "Meeting Link";
      case "EVENT_HOST_DETAILS":
        return "Host Information";
      case "EVENT_UPDATE":
        return "Content";
      case "EVENT_COLLATERALS":
        return "Collateral Information";
      case "THANKYOU":
        return "Thank You Message";
      case "EVENT_SHIPMENTS":
        return "Shipment Details";
      case "POST_EVENT_IMPACT":
        return "Impact Report Content";
      case "ATTENDANCE":
        return "Attendance Details";
      case "VOLUNTEER_SUBMISSION_LINK":
        return "Submission Instructions";
      default:
        return "Content";
    }
  };

  // Get content field placeholder based on update type
  const getContentFieldPlaceholder = () => {
    switch (updateType) {
      case "EVENT_PHOTOS":
        return "Enter Google Drive or cloud storage link with event photos";
      case "EVENT_BROCHURES":
        return "Enter description and any instructions about the event brochures";
      case "EVENT_LOCATION":
        return "Enter detailed information about the new location, including address and directions";
      case "EVENT_MEET_LINK":
        return "Enter the meeting link and any instructions for joining";
      case "EVENT_HOST_DETAILS":
        return "Enter information about the host, including contact details";
      case "EVENT_COLLATERALS":
        return "Enter details about the event collaterals (pamphlets, banners, etc.)";
      case "THANKYOU":
        return "Compose a thank you message for participants and organizers";
      case "EVENT_SHIPMENTS":
        return "Enter details including materials shipped, quantity, shipping method, and tracking information";
      case "POST_EVENT_IMPACT":
        return "Share information about the event's impact and outcomes";
      case "ATTENDANCE":
        return "Enter attendance information or a link to attendance records";
      case "VOLUNTEER_SUBMISSION_LINK":
        return "Enter instructions for volunteers to submit their information or feedback";
      default:
        return "Enter update message content";
    }
  };

  // Get attachment field label based on update type
  const getAttachmentFieldLabel = () => {
    switch (updateType) {
      case "EVENT_PHOTOS":
        return "Additional Photo Link";
      case "EVENT_BROCHURES":
        return "Brochure PDF Link";
      case "EVENT_LOCATION":
        return "Map Link";
      case "EVENT_MEET_LINK":
        return "Alternative Meeting Link";
      case "EVENT_COLLATERALS":
        return "Collateral Files Link";
      case "EVENT_SHIPMENTS":
        return "Tracking Link";
      case "ATTENDANCE":
        return "Attendance Sheet Link";
      case "VOLUNTEER_SUBMISSION_LINK":
        return "Submission Form Link";
      default:
        return "Attachment URL (optional)";
    }
  };

  // Get whether attachment field should look like a URL input
  const isUrlAttachment = () => {
    return [
      "EVENT_PHOTOS",
      "EVENT_LOCATION",
      "EVENT_MEET_LINK",
      "VOLUNTEER_SUBMISSION_LINK",
      "ATTENDANCE",
    ].includes(updateType);
  };

  // Fetch event updates
  const {
    data: eventUpdates,
    isLoading: isUpdatesLoading,
    refetch: refetchUpdates,
    isError: isUpdatesError,
  } = useQuery({
    queryKey: ["event-updates", id],
    queryFn: async () => {
      try {
        const apiUrl = import.meta.env.VITE_CC_API_URL || "";
        const response = await fetch(`${apiUrl}/v1/events/${id}/updates`, {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        });

        if (!response.ok) {
          return { data: [] }; // Return empty array if API fails
        }

        return response.json();
      } catch (error) {
        console.error("Failed to fetch updates:", error);
        return { data: [] }; // Return empty array on error
      }
    },
    enabled: !!id && !!session?.access_token,
    retry: false, // Don't retry if the endpoint doesn't exist yet
  });

  // Mutation to create a new event update
  const createEventUpdate = useMutation({
    mutationFn: async (updateData: {
      updateType: string;
      content: string;
      attachmentUrl?: string;
      sendEmailToBooker: boolean;
      sendEmailToParticipants: boolean;
    }) => {
      setIsSubmittingUpdate(true);

      const apiUrl = import.meta.env.VITE_CC_API_URL || "";

      const response = await fetch(`${apiUrl}/v1/events/${id}/updates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error("Failed to create event update");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Event update sent successfully",
      });
      setIsSubmittingUpdate(false);
      setShowUpdateForm(false);
      resetUpdateForm();
      refetchUpdates();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to send event update",
        variant: "destructive",
      });
      setIsSubmittingUpdate(false);
    },
  });

  // Reset update form fields
  const resetUpdateForm = () => {
    setUpdateType("");
    setUpdateContent("");
    setAttachmentUrl("");
    setSendToBooker(false);
    setSendToParticipants(false);
  };

  // Submit update form
  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();

    if (!updateType || !updateContent) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    createEventUpdate.mutate({
      updateType,
      content: updateContent,
      attachmentUrl: attachmentUrl || undefined,
      sendEmailToBooker: sendToBooker,
      sendEmailToParticipants: sendToParticipants,
    });
  };

  // Helper function to get icon for update type
  const getUpdateTypeIcon = (type: string) => {
    switch (type) {
      case "EVENT_PHOTOS":
        return <Image className="h-5 w-5" />;
      case "EVENT_BROCHURES":
        return <FileImage className="h-5 w-5" />;
      case "EVENT_LOCATION":
        return <MapPinned className="h-5 w-5" />;
      case "EVENT_MEET_LINK":
        return <Video className="h-5 w-5" />;
      case "EVENT_HOST_DETAILS":
        return <User className="h-5 w-5" />;
      case "EVENT_UPDATE":
        return <Info className="h-5 w-5" />;
      case "EVENT_COLLATERALS":
        return <FileText className="h-5 w-5" />;
      case "THANKYOU":
        return <Heart className="h-5 w-5" />;
      case "EVENT_SHIPMENTS":
        return <Package className="h-5 w-5" />;
      case "POST_EVENT_IMPACT":
        return <BarChart className="h-5 w-5" />;
      case "ATTENDANCE":
        return <Users className="h-5 w-5" />;
      case "VOLUNTEER_SUBMISSION_LINK":
        return <ExternalLink className="h-5 w-5" />;
      default:
        return <MessageSquare className="h-5 w-5" />;
    }
  };

  // Helper function to get human-readable update type
  const getUpdateTypeLabel = (type: string) => {
    switch (type) {
      case "EVENT_PHOTOS":
        return "Event Photos";
      case "EVENT_BROCHURES":
        return "Event Brochures";
      case "EVENT_LOCATION":
        return "Location Change";
      case "EVENT_MEET_LINK":
        return "Meeting Link";
      case "EVENT_HOST_DETAILS":
        return "Host Information";
      case "EVENT_UPDATE":
        return "General Update";
      case "EVENT_COLLATERALS":
        return "Event Collaterals";
      case "THANKYOU":
        return "Thank You Message";
      case "EVENT_SHIPMENTS":
        return "Shipment Information";
      case "POST_EVENT_IMPACT":
        return "Impact Report";
      case "ATTENDANCE":
        return "Attendance Update";
      case "VOLUNTEER_SUBMISSION_LINK":
        return "Volunteer Submission";
      default:
        return "Other Update";
    }
  };

  // Helper function to get background color for update type
  const getUpdateTypeBgColor = (type: string) => {
    switch (type) {
      case "EVENT_PHOTOS":
        return "bg-purple-50 border-purple-200";
      case "EVENT_BROCHURES":
        return "bg-indigo-50 border-indigo-200";
      case "EVENT_LOCATION":
        return "bg-yellow-50 border-yellow-200";
      case "EVENT_MEET_LINK":
        return "bg-blue-50 border-blue-200";
      case "EVENT_HOST_DETAILS":
        return "bg-green-50 border-green-200";
      case "EVENT_UPDATE":
        return "bg-slate-50 border-slate-200";
      case "EVENT_COLLATERALS":
        return "bg-cyan-50 border-cyan-200";
      case "THANKYOU":
        return "bg-pink-50 border-pink-200";
      case "EVENT_SHIPMENTS":
        return "bg-amber-50 border-amber-200";
      case "POST_EVENT_IMPACT":
        return "bg-emerald-50 border-emerald-200";
      case "ATTENDANCE":
        return "bg-violet-50 border-violet-200";
      case "VOLUNTEER_SUBMISSION_LINK":
        return "bg-sky-50 border-sky-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const [selectedParticipants, setSelectedParticipants] = React.useState<
    string[]
  >([]);
  const [attendanceRemarks, setAttendanceRemarks] = React.useState("");
  const [bulkActionOpen, setBulkActionOpen] = React.useState(false);
  const [isSubmittingAttendance, setIsSubmittingAttendance] =
    React.useState(false);
  const [updatingParticipants, setUpdatingParticipants] = React.useState<{
    [key: string]: boolean;
  }>({});

  // Mutation for updating a single participant's status
  const updateParticipantStatusMutation = useMutation({
    mutationFn: ({
      participantId,
      status,
      remarks,
    }: {
      participantId: string;
      status: "signed_up" | "attended" | "no_show" | "opt_out";
      remarks?: string;
    }) => {
      return updateParticipantStatus(
        id!,
        participantId,
        status,
        remarks,
        session?.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventAttendance", id] });
      toast({
        title: "Success",
        description: "Participant status updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update participant status: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Mutation for bulk updating participant statuses
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({
      participantIds,
      status,
      remarks,
    }: {
      participantIds: string[];
      status: "signed_up" | "attended" | "no_show" | "opt_out";
      remarks?: string;
    }) => {
      return bulkUpdateParticipantStatus(
        id,
        participantIds,
        status,
        remarks,
        session?.access_token
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["eventAttendance", id] });
      setSelectedParticipants([]);
      setBulkActionOpen(false);
      setAttendanceRemarks("");
      toast({
        title: "Success",
        description: `Successfully updated ${data.count} participant records`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update participant statuses: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Handle single participant status update
  const handleStatusUpdate = (
    participantId: string,
    status: "signed_up" | "attended" | "no_show" | "opt_out"
  ) => {
    setUpdatingParticipants((prev) => ({ ...prev, [participantId]: true }));
    updateParticipantStatusMutation.mutate(
      { participantId, status },
      {
        onSettled: () => {
          setUpdatingParticipants((prev) => ({
            ...prev,
            [participantId]: false,
          }));
        },
      }
    );
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = (
    status: "signed_up" | "attended" | "no_show" | "opt_out"
  ) => {
    if (selectedParticipants.length === 0) {
      toast({
        title: "Warning",
        description: "Please select at least one participant",
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingAttendance(true);
    bulkUpdateStatusMutation.mutate(
      {
        participantIds: selectedParticipants,
        status,
        remarks: attendanceRemarks,
      },
      {
        onSettled: () => {
          setIsSubmittingAttendance(false);
        },
      }
    );
  };

  // Toggle participant selection for bulk actions
  const toggleParticipantSelection = (participantId: string) => {
    setSelectedParticipants((prev) =>
      prev.includes(participantId)
        ? prev.filter((id) => id !== participantId)
        : [...prev, participantId]
    );
  };

  // Select/deselect all participants
  const toggleSelectAll = (checked: boolean) => {
    if (checked && attendanceData) {
      setSelectedParticipants(attendanceData.map((p) => p.id));
    } else {
      setSelectedParticipants([]);
    }
  };

  // State for add participant dialog
  const [addParticipantOpen, setAddParticipantOpen] = React.useState(false);
  const [newParticipant, setNewParticipant] = React.useState({
    name: "",
    email: "",
    phone: "",
  });

  // State for bulk upload dialog
  const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);
  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [parsedCsvData, setParsedCsvData] = React.useState<Array<any>>([]);
  const [csvUploadStep, setCsvUploadStep] = React.useState<
    "upload" | "review" | "processing"
  >("upload");
  const csvFileInputRef = React.useRef<HTMLInputElement>(null);

  // Mutation for adding a single participant
  const addParticipantMutation = useMutation({
    mutationFn: (participantData: {
      name: string;
      email: string;
      phone?: string;
    }) => {
      return addParticipant(id!, participantData, session?.access_token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventAttendance", id] });
      setAddParticipantOpen(false);
      setNewParticipant({
        name: "",
        email: "",
        phone: "",
      });
      toast({
        title: "Success",
        description: "Participant added successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to add participant: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Mutation for bulk adding participants
  const bulkAddParticipantsMutation = useMutation({
    mutationFn: (
      participants: Array<{
        name: string;
        email: string;
        phone?: string;
        status?: "signed_up" | "attended" | "no_show" | "opt_out";
      }>
    ) => {
      return bulkAddParticipants(id!, participants, session?.access_token);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["eventAttendance", id] });
      setBulkUploadOpen(false);
      setCsvFile(null);
      setParsedCsvData([]);
      setCsvUploadStep("upload");
      toast({
        title: "Success",
        description: `Successfully added ${data.data.count} participants`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to bulk add participants: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Handle file upload and parsing
  const handleCsvFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedData = results.data.map((row: any) => ({
            name: row.name || "",
            email: row.email || "",
            phone: row.phone || "",
            status:
              row.status &&
              ["signed_up", "attended", "no_show", "opt_out"].includes(
                row.status
              )
                ? row.status
                : "signed_up",
          }));

          setParsedCsvData(parsedData);
          setCsvUploadStep("review");
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          toast({
            title: "Error",
            description: "Please check your file format and try again.",
            variant: "destructive",
          });
        },
      });
    }
  };

  // Handle submit for adding a single participant
  const handleAddParticipant = (e: React.FormEvent) => {
    e.preventDefault();
    addParticipantMutation.mutate(newParticipant);
  };

  // Handle submit for bulk upload
  const handleBulkUpload = () => {
    setCsvUploadStep("processing");
    bulkAddParticipantsMutation.mutate(parsedCsvData);
  };

  // Reset file input
  const resetFileInput = () => {
    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = "";
    }
    setCsvFile(null);
    setParsedCsvData([]);
    setCsvUploadStep("upload");
  };

  // State for search and filter
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  // Filter participants based on search and status
  const filteredParticipants = React.useMemo(() => {
    if (!attendanceData) return [];

    return attendanceData.filter((participant) => {
      // Status filter
      if (statusFilter !== "all" && participant.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        return (
          participant.name.toLowerCase().includes(searchLower) ||
          participant.email.toLowerCase().includes(searchLower) ||
          (participant.phone && participant.phone.includes(searchQuery))
        );
      }

      return true;
    });
  }, [attendanceData, searchQuery, statusFilter]);

  // Helper function to get owner badge color
  const getOwnerBadgeColor = (owner: string) => {
    switch (owner) {
      case "npo":
        return "bg-purple-100 text-purple-800";
      case "corporate":
        return "bg-blue-100 text-blue-800";
      case "host":
        return "bg-green-100 text-green-800";
      case "cc":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to get owner label
  const getOwnerLabel = (owner: string) => {
    switch (owner) {
      case "npo":
        return "NPO";
      case "corporate":
        return "Corporate";
      case "host":
        return "Host";
      case "cc":
        return "CauseConnect";
      default:
        return owner;
    }
  };

  // Helper function to parse timeline to days
  const parseTimelineToDays = (timeline: string): number => {
    const match = timeline.match(/T-(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  // Helper function to calculate the actual date from T-x format
  const getTimelineDate = (timeline: string): Date | null => {
    if (!eventResponse || !timeline) return null;

    // Get the event date (using start_date as the primary reference)
    const eventDateStr = (eventResponse as any).event_date;
    if (!eventDateStr) return null;
    const eventDate = new Date(eventDateStr);

    // Handle "During" timeline string
    if (timeline.toLowerCase() === "during") {
      return new Date(eventDate);
    }

    // Handle timeline with T+ format (post-event)
    const postMatch = timeline.match(/T\+(\d+)/);
    if (postMatch) {
      const days = parseInt(postMatch[1]);
      const targetDate = new Date(eventDate);
      targetDate.setDate(eventDate.getDate() + days);
      return targetDate;
    }

    // Handle timeline with T- format (pre-event)
    const preMatch = timeline.match(/T-(\d+)/);
    if (preMatch) {
      const days = parseInt(preMatch[1]);
      const targetDate = new Date(eventDate);
      targetDate.setDate(eventDate.getDate() - days);
      return targetDate;
    }

    return null;
  };

  // Helper function to get days remaining or passed
  const getDaysMessage = (
    timeline: string
  ): { status: string; message: string } => {
    const timelineDate = getTimelineDate(timeline);
    if (!timelineDate) return { status: "", message: "" };

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
    timelineDate.setHours(0, 0, 0, 0);

    const diffTime = timelineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        status: "overdue",
        message: `Overdue (${Math.abs(diffDays)} day${
          Math.abs(diffDays) !== 1 ? "s" : ""
        } ago)`,
      };
    } else if (diffDays === 0) {
      return { status: "today", message: "Due today" };
    } else {
      return {
        status: "upcoming",
        message: `Upcoming (in ${diffDays} day${diffDays !== 1 ? "s" : ""})`,
      };
    }
  };

  // Helper function to check if item is overdue
  const isOverdue = (timeline: string, completed: boolean): boolean => {
    if (completed) return false;
    const timelineInfo = getDaysMessage(timeline);
    return timelineInfo.status === "overdue";
  };

  // Filter and group checklist items by phase
  const filteredAndGroupedChecklistItems = React.useMemo(() => {
    if (!checklistData?.checklist?.items)
      return { Pre: [], During: [], Post: [] };

    let items = [...checklistData.checklist.items];

    // Filter items based on user profile type
    if (profile?.user_type) {
      items = items.filter((item) => {
        switch (profile.user_type) {
          case "nonprofit":
            return item.owner === "npo" || item.owner === "cc";
          case "company":
            return item.owner === "corporate" || item.owner === "cc";
          case "host":
            return item.owner === "host";
          default:
            return true;
        }
      });
    }

    // Apply owner filter (if user wants to further filter their own items)
    if (checklistOwnerFilter !== "all") {
      items = items.filter((item) => item.owner === checklistOwnerFilter);
    }

    // Apply event type filter based on checklist data's eventType
    if (
      checklistEventTypeFilter !== "all" &&
      checklistData?.checklist?.eventType
    ) {
      const eventType = checklistData.checklist.eventType.toLowerCase();
      if (
        checklistEventTypeFilter === "inOffice" &&
        eventType.includes("in-office")
      ) {
        items = items.filter((item) => item.inOffice);
      } else if (
        checklistEventTypeFilter === "outdoor" &&
        eventType.includes("outdoor")
      ) {
        items = items.filter((item) => item.outdoor);
      } else if (
        checklistEventTypeFilter === "virtual" &&
        eventType.includes("virtual")
      ) {
        items = items.filter((item) => item.virtual);
      }
    }

    // Group items by phase
    const grouped = {
      Pre: items.filter((item) => item.eventPhase === "Pre"),
      During: items.filter((item) => item.eventPhase === "During"),
      Post: items.filter((item) => item.eventPhase === "Post"),
    };

    // Sort items within each phase
    Object.keys(grouped).forEach((phase) => {
      grouped[phase as keyof typeof grouped].sort((a, b) => {
        if (checklistSortBy === "timeline") {
          const aDays = parseTimelineToDays(a.timeline);
          const bDays = parseTimelineToDays(b.timeline);
          return bDays - aDays; // Higher days first (T-7 before T-1)
        } else if (checklistSortBy === "status") {
          // Incomplete items first
          if (a.completed !== b.completed) {
            return a.completed ? 1 : -1;
          }
          // Then by timeline
          const aDays = parseTimelineToDays(a.timeline);
          const bDays = parseTimelineToDays(b.timeline);
          return bDays - aDays;
        }
        return 0;
      });
    });

    return grouped;
  }, [
    checklistData,
    checklistOwnerFilter,
    checklistSortBy,
    checklistEventTypeFilter,
  ]);

  if (isEventLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (eventError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="text-destructive h-12 w-12 mb-4" />
        <h1 className="text-xl font-semibold">Error loading event</h1>
        <p className="text-muted-foreground">
          {(eventError as Error).message || "An error occurred"}
        </p>
      </div>
    );
  }

  if (!eventResponse) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="text-destructive h-12 w-12 mb-4" />
        <h1 className="text-xl font-semibold">Event not found</h1>
        <p className="text-muted-foreground">
          The event you're looking for doesn't exist or has been removed.
        </p>
      </div>
    );
  }

  const event = eventResponse as any;

  const lifecycleStages = [
    {
      status: "payment_pending",
      icon: DollarSign,
      description: "Awaiting payment confirmation",
    },
    {
      status: "payment_done",
      icon: CheckCircle2,
      description: "Payment completed, event is booked",
    },
    {
      status: "event_confirmation_in_progress",
      icon: Clock,
      description: "Confirming event details",
    },
    {
      status: "event_confirmed",
      icon: CheckCircle2,
      description: "Event details confirmed",
    },
    {
      status: "event_in_progress",
      icon: BarChart,
      description: "Event is currently happening",
    },
    {
      status: "event_complete",
      icon: Star,
      description: "Event has been completed",
    },
  ];

  const conditionalStages = [
    {
      status: "payment_failed",
      icon: XCircle,
      description: "Payment processing failed",
    },
    {
      status: "event_cancelled",
      icon: XCircle,
      description: "Event has been cancelled",
    },
  ];

  const currentStage = event.status;
  const currentStageIndex = lifecycleStages.findIndex(
    (stage) => stage.status === currentStage
  );

  const today = new Date();
  // Handle the case where event_date might already include time and timezone
  let eventDate;
  try {
    // First, check if event_date is a complete ISO string with time
    eventDate = parseISO(event.event_date);
  } catch (error) {
    console.error("Error parsing event date/time:", error);
    // Fallback to current date if parsing fails
    eventDate = new Date();
  }

  const isUpcoming = isAfter(eventDate, today);
  const isToday = isSameDay(eventDate, today);
  const isPast = isBefore(eventDate, today) && !isToday;

  const daysUntil = Math.ceil(
    (eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  const formattedDate = format(eventDate, "EEEE, MMMM d, yyyy");
  const formattedTime = format(eventDate, "h:mm a");

  const participantCount = event.event_participants?.length || 0;

  const pendingNpoInvite = event.event_npo_invites?.find(
    (invite) => invite.status === "pending"
  );
  const hasInviteToRespond = !!pendingNpoInvite;

  const missingInformation = [];

  if (
    !event.location &&
    [
      "payment_pending",
      "payment_done",
      "event_confirmation_in_progress",
    ].includes(currentStage)
  ) {
    missingInformation.push("Event location");
  }

  const needsAttention =
    currentStage !== "event_cancelled" &&
    ((hasInviteToRespond && profile?.user_type == "nonprofit") ||
      missingInformation.length > 0);

  // Format meta key to title case (e.g., team_name -> Team Name)
  const formatMetaKey = (key: string) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="space-y-6">
      <LoadingDialog
        open={isLoading}
        title="Processing"
        description="Please wait while we process your request..."
      />

      {needsAttention && (
        <Card className="border-orange-200 bg-orange-50 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-orange-500" />
              Action Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasInviteToRespond && profile?.user_type === "nonprofit" && (
              <div className="flex flex-col p-3 bg-white rounded-md border border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-3 text-orange-500" />
                    <div>
                      <h3 className="text-lg font-medium">
                        Event Invitation Pending
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Your organization has been invited to participate in
                        this event. Please respond to the invitation.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3 justify-end mt-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleInviteResponse(pendingNpoInvite.id, "declined")
                    }
                    className="text-red-500 hover:text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-2" /> Decline
                  </Button>
                  <Button
                    onClick={() =>
                      handleInviteResponse(pendingNpoInvite.id, "accepted")
                    }
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="h-4 w-4 mr-2" /> Accept
                  </Button>
                </div>
              </div>
            )}

            {missingInformation.length > 0 && (
              <div className="flex flex-col p-3 bg-white rounded-md border border-orange-200">
                <div className="flex items-center mb-2">
                  <AlertCircle className="h-5 w-5 mr-3 text-orange-500" />
                  <div>
                    <h3 className="text-lg font-medium">
                      Missing Event Information
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      The following information is needed to complete this
                      event:
                    </p>
                  </div>
                </div>
                <ul className="ml-8 list-disc text-sm">
                  {missingInformation.map((item, index) => (
                    <li key={index} className="text-gray-700">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cancel Event Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-destructive">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Cancel Event
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this event? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border border-destructive/20 rounded-md bg-destructive/10 my-4">
            <h3 className="font-medium mb-2">Event Details</h3>
            <p className="text-sm text-gray-700">{event.event_name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formattedDate} at {formattedTime}
            </p>
          </div>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">
                Please provide a reason for cancellation:
              </h4>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation"
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleCancelEvent}
              disabled={!cancelReason.trim()}
            >
              Yes, Cancel Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Event Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-primary">
              <Play className="h-5 w-5 mr-2" />
              Start Event
            </DialogTitle>
            <DialogDescription>
              {isToday ? (
                "Are you sure you want to start this event now?"
              ) : (
                <>
                  <span className="text-red-500 font-semibold">Warning:</span>{" "}
                  <span className="text-red-500">
                    This event is not scheduled for today. Starting it now may
                    cause confusion.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border border-primary/20 rounded-md bg-primary/10 my-4">
            <h3 className="font-medium mb-2">Event Details</h3>
            <p className="text-sm text-gray-700">{event.event_name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formattedDate} at {formattedTime}
            </p>
          </div>

          {!isToday && (
            <div className="space-y-2 mb-4">
              <Label htmlFor="confirmText">
                To confirm, type{" "}
                <span className="font-medium bg-primary/20 p-2 rounded">
                  i agree to start the event
                </span>
              </Label>
              <Input
                id="confirmText"
                value={startConfirmText}
                onChange={(e) => setStartConfirmText(e.target.value)}
                placeholder="Type confirmation text here"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowStartDialog(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="default" onClick={handleStartEvent}>
              Yes, Start Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Event Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-green-600">
              <Star className="h-5 w-5 mr-2" />
              Complete Event
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this event as completed?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border border-green-200 rounded-md bg-green-50 my-4">
            <h3 className="font-medium mb-2">Event Details</h3>
            <p className="text-sm text-gray-700">{event.event_name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formattedDate} at {formattedTime}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCompleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleCompleteEvent}
            >
              Yes, Mark as Completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        className={`p-4 rounded-lg flex items-center justify-between ${
          currentStage === "payment_pending"
            ? "bg-yellow-50 border border-yellow-200"
            : currentStage === "payment_failed" ||
              currentStage === "event_cancelled"
            ? "bg-red-50 border border-red-200"
            : currentStage === "event_complete"
            ? "bg-green-50 border border-green-200"
            : "bg-blue-50 border border-blue-200"
        }`}
      >
        <div className="flex items-center space-x-3">
          {currentStage === "payment_pending" && (
            <AlertCircle className="h-6 w-6 text-yellow-500" />
          )}
          {(currentStage === "payment_failed" ||
            currentStage === "event_cancelled") && (
            <XCircle className="h-6 w-6 text-red-500" />
          )}
          {currentStage === "event_complete" && (
            <CheckCircle2 className="h-6 w-6 text-green-500" />
          )}
          {![
            "payment_pending",
            "payment_failed",
            "event_cancelled",
            "event_complete",
          ].includes(currentStage) && (
            <HelpCircle className="h-6 w-6 text-blue-500" />
          )}
          <div>
            <h2 className="font-semibold text-lg">
              Event Status: {formatStatus(currentStage)}
            </h2>
            <p className="text-sm">
              {currentStage === "payment_pending" &&
                "Payment is required to confirm this event. Please complete the payment process."}
              {currentStage === "payment_done" &&
                "Payment has been successfully processed. Your event is confirmed."}
              {currentStage === "payment_failed" &&
                "There was an issue with your payment. Please check and try again."}
              {currentStage === "event_booked" &&
                "Your event has been booked! You're all set for the big day."}
              {currentStage === "event_confirmation_in_progress" &&
                "We're currently confirming the details of your event."}
              {currentStage === "event_confirmed" &&
                "Your event is confirmed and ready to go!"}
              {currentStage === "event_in_progress" &&
                "This event is currently happening. Enjoy!"}
              {currentStage === "event_complete" &&
                "This event has been completed. Thank you for participating!"}
              {currentStage === "event_cancelled" &&
                "This event has been cancelled."}
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`text-sm px-3 py-1 ${getStatusColor(currentStage)}`}
        >
          {formatStatus(currentStage)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 border-none shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-3xl font-bold">
              {event.event_name}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="w-full break-words">
                    {event.description?.slice(0, 200)}
                    {event.description?.length > 200 && "..."}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="max-w-xs break-words whitespace-pre-wrap">
                    {event.description}
                  </span>
                </TooltipContent>
              </Tooltip>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{formattedDate}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">{formattedTime}</p>
                </div>
              </div>
              {event.location && (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <div className="font-medium">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            {event.location.startsWith("http") ? (
                              <a
                                href={event.location}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline break-all inline"
                              >
                                {event.location.length > 40
                                  ? `${event.location.substring(0, 40)}...`
                                  : event.location}
                              </a>
                            ) : (
                              <span className="break-words">
                                {event.location.length > 40
                                  ? `${event.location.substring(0, 40)}...`
                                  : event.location}
                              </span>
                            )}
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-md">
                            {event.location}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {event.meta?.npo_location && (
                      <p className="text-sm text-muted-foreground">
                        {event.meta.npo_location
                          .split("-")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .reverse()
                          .join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {event.opportunity_catalog && (
              <>
                <div className="flex flex-wrap gap-6 pt-2">
                  <div className="flex items-center space-x-2">
                    <Clock4 className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Duration</p>
                      <p className="font-medium">
                        {event.opportunity_catalog.duration}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Building className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Format</p>
                      <p className="font-medium">
                        {event.opportunity_catalog.format}
                      </p>
                    </div>
                  </div>
                  {event.opportunity_catalog.event_cost !== undefined && (
                    <div className="flex items-center space-x-2">
                      <Banknote className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Cost</p>
                        {event.opportunity_catalog.event_cost === 0 ? (
                          <p className="font-medium">Book Now Pay Later</p>
                        ) : (
                          <p className="font-medium">
                            {event.meta?.currency || "INR"}{" "}
                            {event.opportunity_catalog.event_cost}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Add volunteer count */}
                  {event.expected_participants && (
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Expected Participants
                        </p>
                        <p className="font-medium">
                          {event.expected_participants}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 mr-2 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Volunteer Output
                  </p>
                  <p className="font-small">
                    {event.opportunity_catalog.volunteer_output}
                  </p>
                </div>

                {event.opportunity_catalog.faqs && (
                  <div className="flex items-center space-x-2">
                    <p className="text-lg font-semibold mb-2 flex items-center">
                      <HelpCircle className="h-5 w-5 mr-2 text-primary" />
                      FAQs
                    </p>
                    <p className="text-sm whitespace-pre-line">
                      {event.opportunity_catalog.faqs}
                    </p>
                  </div>
                )}

                {event.opportunity_catalog.prerequisites &&
                  event.opportunity_catalog.prerequisites.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <p className="text-lg font-semibold">Prerequisites</p>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {event.opportunity_catalog.prerequisites.map(
                          (prereq, index) => (
                            <li key={index}>{prereq}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
              </>
            )}
            {/* Meta Data Section - Only show if meta exists */}
            {event?.meta && Object.keys(event.meta).length > 0 && (
              <div className="p-4 rounded-lg mb-6 border-t">
                <div className="grid grid-cols-5 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {Object.entries(event.meta).map(
                    ([key, value]) =>
                      value && (
                        <div key={key} className="space-y-1">
                          <p className="text-sm font-medium text-muted-foreground">
                            {formatMetaKey(key)}
                          </p>
                          <p className="text-sm font-medium">{String(value)}</p>
                        </div>
                      )
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card
            className={`border-none shadow-lg ${
              event.status === "event_complete"
                ? "bg-green-50"
                : isPast
                ? "bg-gray-50"
                : isToday
                ? "bg-blue-50"
                : isUpcoming && daysUntil <= 7
                ? "bg-yellow-50"
                : "bg-green-50"
            }`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium">
                  {event.status === "event_complete"
                    ? "Event Completed"
                    : isPast
                    ? "Event Not Held"
                    : isToday
                    ? "Event is Today!"
                    : isUpcoming
                    ? `${daysUntil} days until event`
                    : "Event Status"}
                </CardTitle>

                {/* Event Control Buttons */}
                <div className="flex items-center gap-2">
                  {/* Cancel Event Button */}
                  {![
                    "event_complete",
                    "event_cancelled",
                    "payment_failed",
                  ].includes(currentStage) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                  )}

                  {/* Start Event Button */}
                  {currentStage === "event_confirmed" && (
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowStartDialog(true)}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Start Event
                    </Button>
                  )}

                  {/* Complete Event Button */}
                  {currentStage === "event_in_progress" && (
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setShowCompleteDialog(true)}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Complete
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {event.status === "event_complete" ? (
                  <div className="flex items-center">
                    <CheckCircle2 className="h-8 w-8 text-green-500 mr-2" />
                    <span>Complete</span>
                  </div>
                ) : isPast ? (
                  <div className="flex items-center">
                    <XCircle className="h-8 w-8 text-red-500 mr-2" />
                    <span>Not Held</span>
                  </div>
                ) : isToday ? (
                  <div className="flex items-center">
                    <AlertCircle className="h-8 w-8 text-blue-500 mr-2" />
                    <span>Today</span>
                  </div>
                ) : isUpcoming ? (
                  <div className="flex items-center">
                    <Calendar className="h-8 w-8 text-primary mr-2" />
                    <span>{daysUntil} days</span>
                  </div>
                ) : (
                  formatStatus(currentStage)
                )}
              </div>
            </CardContent>
          </Card>

          {/* Event Links Card */}
          <Card className="border-none shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-medium flex items-center">
                <Link className="h-5 w-5 mr-2 text-primary" />
                Event Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sign Up Section */}
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center">
                  <User className="h-4 w-4 mr-2 text-primary" />
                  Volunteer Sign up Link
                </h3>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={`https://${
                      event?.npo?.domains?.domain_suffix || `npo`
                    }.causeconnect.ai/events/${event?.npo?.slug}/${
                      event.slug
                    }/register`}
                    className="text-sm bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `https://${
                          event?.npo?.domains?.domain_suffix || `npo`
                        }.causeconnect.ai/events/${event?.npo?.slug}/${
                          event.slug
                        }`
                      );
                      toast({
                        title: "Link copied",
                        description: "Sign up link copied to clipboard",
                      });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Event Feedback Section */}
              <div className="mt-6 pt-6 border-t">
                <div>
                  <h3 className="text-sm font-medium mb-2 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2 text-primary" />
                    Feedback
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Input
                          readOnly
                          value={`https://${
                            event?.npo?.domains?.domain_suffix || `npo`
                          }.causeconnect.ai/feedback/volunteer/${event.id}`}
                          className="text-sm bg-gray-50"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `https://${
                                event?.npo?.domains?.domain_suffix || `npo`
                              }.causeconnect.ai/feedback/volunteer/${event.id}`
                            );
                            toast({
                              title: "Link copied",
                              description: "Feedback link copied to clipboard",
                            });
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(
                            `https://${
                              event?.npo?.domains?.domain_suffix || `npo`
                            }.causeconnect.ai/feedback/volunteer/${event.id}`
                          )}`;

                          // Create a temporary anchor element
                          const a = document.createElement("a");
                          a.href = qrCodeUrl;
                          a.download = `feedback-qr-code-${event.id}.png`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);

                          toast({
                            title: "QR Code Download",
                            description: "QR code download started",
                          });
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download QR (500x500)
                      </Button>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-md flex-shrink-0">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(
                          `https://${
                            event?.npo?.domains?.domain_suffix || `npo`
                          }.causeconnect.ai/feedback/volunteer/${event.id}`
                        )}`}
                        alt="Feedback QR Code"
                        className="h-20 w-20"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Standalone Feedback Section for Completed Events */}
      {currentStage === "event_complete" && (
        <Card className="border shadow-lg my-6 w-full overflow-hidden">
          <CardHeader className="pb-4 pt-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50 px-8">
            <CardTitle className="text-xl font-semibold text-slate-800 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 mr-2 text-primary" />
              Event Feedback Summary
            </CardTitle>
            <div className="text-sm text-muted-foreground text-center">
              View aggregated feedback from participants and bookers
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <div className="space-y-6">
              {isFeedbackLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : feedbackError ? (
                <div className="text-center py-8 bg-red-50 rounded-lg border border-dashed border-red-200">
                  <AlertCircle className="h-10 w-10 text-red-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-red-700">
                    Error Loading Feedback
                  </h3>
                  <p className="text-sm text-red-500 mt-1 mb-4">
                    {feedbackError instanceof Error
                      ? feedbackError.message
                      : "Failed to load feedback"}
                  </p>
                </div>
              ) : feedbackSummary?.data?.total_responses > 0 ? (
                <div>
                  <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-blue-800">
                        Overall Feedback
                      </h3>
                      <Badge
                        variant="outline"
                        className="bg-blue-100 text-blue-800 border-blue-200"
                      >
                        {feedbackSummary?.data?.total_responses || 0} Total
                        Responses
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-md p-4 border border-blue-100 shadow-sm">
                        <div className="flex items-center mb-2">
                          <User className="h-5 w-5 text-blue-600 mr-2" />
                          <h4 className="font-medium">Booker Feedback</h4>
                          <Badge
                            variant="outline"
                            className="ml-2 text-xs bg-blue-50"
                          >
                            {feedbackSummary?.data?.booker_feedback?.count || 0}{" "}
                            responses
                          </Badge>
                        </div>

                        {feedbackSummary?.data?.booker_feedback?.count > 0 ? (
                          <div className="space-y-3 mt-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-slate-600">
                                  Booking Ease
                                </span>
                                <span className="text-sm font-medium">
                                  {(
                                    feedbackSummary?.data?.booker_feedback
                                      ?.avg_booking_ease || 0
                                  ).toFixed(1)}
                                  /5
                                </span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{
                                    width: `${
                                      ((feedbackSummary?.data?.booker_feedback
                                        ?.avg_booking_ease || 0) /
                                        5) *
                                      100
                                    }%`,
                                  }}
                                ></div>
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-slate-600">
                                  Logistics
                                </span>
                                <span className="text-sm font-medium">
                                  {(
                                    feedbackSummary?.data?.booker_feedback
                                      ?.avg_logistics || 0
                                  ).toFixed(1)}
                                  /5
                                </span>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{
                                    width: `${
                                      ((feedbackSummary?.data?.booker_feedback
                                        ?.avg_logistics || 0) /
                                        5) *
                                      100
                                    }%`,
                                  }}
                                ></div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                              <div className="bg-blue-50 p-3 rounded-md text-center">
                                <div className="text-xl font-bold text-blue-700">
                                  {(
                                    feedbackSummary?.data?.booker_feedback
                                      ?.met_expectations_pct || 0
                                  ).toFixed(0)}
                                  %
                                </div>
                                <div className="text-xs text-slate-600">
                                  Met Expectations
                                </div>
                              </div>

                              <div className="bg-blue-50 p-3 rounded-md text-center">
                                <div className="text-xl font-bold text-blue-700">
                                  {(
                                    feedbackSummary?.data?.booker_feedback
                                      ?.clear_communication_pct || 0
                                  ).toFixed(0)}
                                  %
                                </div>
                                <div className="text-xs text-slate-600">
                                  Clear Communication
                                </div>
                              </div>

                              <div className="bg-blue-50 p-3 rounded-md text-center">
                                <div className="text-xl font-bold text-blue-700">
                                  {(
                                    feedbackSummary?.data?.booker_feedback
                                      ?.would_book_again_pct || 0
                                  ).toFixed(0)}
                                  %
                                </div>
                                <div className="text-xs text-slate-600">
                                  Would Book Again
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 italic mt-2">
                            No booker feedback available
                          </p>
                        )}
                      </div>

                      {feedbackSummary?.data?.volunteer_feedback && (
                        <div className="bg-white rounded-md p-4 border border-green-100 shadow-sm">
                          <div className="flex items-center mb-2">
                            <Users className="h-5 w-5 text-green-600 mr-2" />
                            <h4 className="font-medium">Volunteer Feedback</h4>
                            <Badge
                              variant="outline"
                              className="ml-2 text-xs bg-green-50 text-green-700 border-green-200"
                            >
                              {feedbackSummary?.data?.volunteer_feedback
                                ?.count || 0}{" "}
                              responses
                            </Badge>
                          </div>

                          {feedbackSummary?.data?.volunteer_feedback?.count >
                          0 ? (
                            <div className="space-y-3 mt-3">
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm text-slate-600">
                                    Overall Experience
                                  </span>
                                  <span className="text-sm font-medium">
                                    {(
                                      feedbackSummary?.data?.volunteer_feedback
                                        ?.avg_experience || 0
                                    ).toFixed(1)}
                                    /5
                                  </span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                  <div
                                    className="bg-green-600 h-2 rounded-full"
                                    style={{
                                      width: `${
                                        ((feedbackSummary?.data
                                          ?.volunteer_feedback
                                          ?.avg_experience || 0) /
                                          5) *
                                        100
                                      }%`,
                                    }}
                                  ></div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                                <div className="bg-green-50 p-3 rounded-md text-center">
                                  <div className="text-xl font-bold text-green-700">
                                    {(
                                      feedbackSummary?.data?.volunteer_feedback
                                        ?.team_connection_pct || 0
                                    ).toFixed(0)}
                                    %
                                  </div>
                                  <div className="text-xs text-slate-600">
                                    Team Connection
                                  </div>
                                </div>

                                <div className="bg-green-50 p-3 rounded-md text-center">
                                  <div className="text-xl font-bold text-green-700">
                                    {(
                                      feedbackSummary?.data?.volunteer_feedback
                                        ?.company_connection_pct || 0
                                    ).toFixed(0)}
                                    %
                                  </div>
                                  <div className="text-xs text-slate-600">
                                    Company Connection
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic mt-2">
                              No volunteer feedback available
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-slate-700">
                    No Feedback Yet
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    Feedback will appear here once participants submit their
                    responses
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compact Event Lifecycle */}
      <Card className="border-none shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">Event Status</h3>
              <Badge variant="outline" className={getStatusColor(currentStage)}>
                {formatStatus(currentStage)}
              </Badge>
            </div>
          </div>
          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2 z-0"></div>
            <div className="flex justify-between relative z-10">
              {lifecycleStages.map((stage, index) => {
                const IconComponent = stage.icon;
                const isActive = stage.status === currentStage;
                const currentIndex = lifecycleStages.findIndex(
                  (s) => s.status === currentStage
                );
                const isPast = currentIndex > index;

                return (
                  <div
                    key={index}
                    className="flex flex-col items-center space-y-1"
                  >
                    <div
                      className={`rounded-full p-2 flex items-center justify-center ${
                        isActive
                          ? "bg-primary text-white"
                          : isPast
                          ? "bg-primary/20 text-primary"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <span
                      className={`text-xs text-center ${
                        isActive
                          ? "font-semibold text-primary"
                          : "text-gray-500"
                      }`}
                    >
                      {formatStatus(stage.status)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="checklist" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Event Attendance</CardTitle>
              <CardDescription>
                Manage and view all participants registered for this event
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium">Participants</h3>
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200"
                    >
                      {attendanceData?.length || 0} total
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAddParticipantOpen(true)}
                      className="bg-white"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Add Participant
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkUploadOpen(true)}
                      className="bg-white"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Bulk Upload
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        window.open(
                          "/templates/participant-upload-template.csv",
                          "_blank"
                        )
                      }
                      className="bg-white"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>

                {/* Participant management section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold tracking-tight">
                      Participants
                    </h2>
                  </div>

                  <ParticipantStats participants={filteredParticipants} />

                  <ParticipantFilters
                    searchQuery={searchQuery}
                    statusFilter={statusFilter}
                    onSearchChange={setSearchQuery}
                    onStatusFilterChange={setStatusFilter}
                  />

                  {isLoadingAttendance ? (
                    <div className="rounded-md border border-gray-200 p-4">
                      <div className="flex justify-center py-8">
                        <p className="text-sm text-gray-500">
                          Loading participants...
                        </p>
                      </div>
                    </div>
                  ) : filteredParticipants.length > 0 ? (
                    <ParticipantTable
                      participants={filteredParticipants}
                      selectedParticipants={selectedParticipants}
                      updatingParticipants={updatingParticipants}
                      onStatusUpdate={handleStatusUpdate}
                      onParticipantSelect={toggleParticipantSelection}
                      onSelectAll={toggleSelectAll}
                      eventStatus={eventResponse?.status}
                    />
                  ) : (
                    <div className="rounded-md border border-gray-200 p-4">
                      <div className="flex justify-center py-8">
                        <p className="text-sm text-gray-500">
                          No participants found
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist">
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl flex items-center">
                      <ListChecks className="h-5 w-5 mr-2 text-primary" />
                      Event Checklist
                      {checklistData?.checklist?.items && (
                        <Badge className="ml-2 bg-primary/10 text-primary border-none px-3 py-1">
                          {checklistSummary.percentage}% Complete
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Track preparation tasks and responsibilities
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pb-6">
                {isChecklistLoading ? (
                  <div className="flex justify-center items-center py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-primary/70" />
                  </div>
                ) : checklistError ? (
                  <div className="text-center py-10">
                    <AlertCircle className="h-8 w-8 text-destructive/70 mx-auto mb-2" />
                    <p className="text-muted-foreground">
                      Failed to load checklist
                    </p>
                  </div>
                ) : !checklistData || !checklistData.checklist?.items ? (
                  <div className="text-center py-10">
                    <ListChecks className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">
                      No checklist items available
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-purple-700">
                                NPO
                              </p>
                              <h4 className="text-xl font-bold text-purple-900 mt-1">
                                {checklistSummary.byOwner.npo.completed} of{" "}
                                {checklistSummary.byOwner.npo.total}
                              </h4>
                            </div>
                            <Badge
                              className={`${
                                checklistSummary.byOwner.npo.total > 0
                                  ? "bg-purple-200 text-purple-800"
                                  : "bg-slate-200 text-slate-600"
                              } border-none px-3 py-1`}
                            >
                              {checklistSummary.byOwner.npo.total > 0
                                ? Math.round(
                                    (checklistSummary.byOwner.npo.completed /
                                      checklistSummary.byOwner.npo.total) *
                                      100
                                  )
                                : 0}
                              %
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-blue-700">
                                Corporate
                              </p>
                              <h4 className="text-xl font-bold text-blue-900 mt-1">
                                {checklistSummary.byOwner.corporate.completed}{" "}
                                of {checklistSummary.byOwner.corporate.total}
                              </h4>
                            </div>
                            <Badge
                              className={`${
                                checklistSummary.byOwner.corporate.total > 0
                                  ? "bg-blue-200 text-blue-800"
                                  : "bg-slate-200 text-slate-600"
                              } border-none px-3 py-1`}
                            >
                              {checklistSummary.byOwner.corporate.total > 0
                                ? Math.round(
                                    (checklistSummary.byOwner.corporate
                                      .completed /
                                      checklistSummary.byOwner.corporate
                                        .total) *
                                      100
                                  )
                                : 0}
                              %
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-green-700">
                                Host
                              </p>
                              <h4 className="text-xl font-bold text-green-900 mt-1">
                                {checklistSummary.byOwner.host.completed} of{" "}
                                {checklistSummary.byOwner.host.total}
                              </h4>
                            </div>
                            <Badge
                              className={`${
                                checklistSummary.byOwner.host.total > 0
                                  ? "bg-green-200 text-green-800"
                                  : "bg-slate-200 text-slate-600"
                              } border-none px-3 py-1`}
                            >
                              {checklistSummary.byOwner.host.total > 0
                                ? Math.round(
                                    (checklistSummary.byOwner.host.completed /
                                      checklistSummary.byOwner.host.total) *
                                      100
                                  )
                                : 0}
                              %
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-orange-700">
                                CauseConnect
                              </p>
                              <h4 className="text-xl font-bold text-orange-900 mt-1">
                                {checklistSummary.byOwner.cc.completed} of{" "}
                                {checklistSummary.byOwner.cc.total}
                              </h4>
                            </div>
                            <Badge
                              className={`${
                                checklistSummary.byOwner.cc.total > 0
                                  ? "bg-orange-200 text-orange-800"
                                  : "bg-slate-200 text-slate-600"
                              } border-none px-3 py-1`}
                            >
                              {checklistSummary.byOwner.cc.total > 0
                                ? Math.round(
                                    (checklistSummary.byOwner.cc.completed /
                                      checklistSummary.byOwner.cc.total) *
                                      100
                                  )
                                : 0}
                              %
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full mb-6">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-slate-500">
                          Overall Progress
                        </span>
                        <span className="text-sm font-medium">
                          {checklistSummary.percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2.5">
                        <div
                          className="bg-primary h-2.5 rounded-full"
                          style={{ width: `${checklistSummary.percentage}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Checklist Items */}
                    <div className="space-y-4">
                      {Object.keys(filteredAndGroupedChecklistItems).map(
                        (phase) => (
                          <div key={phase} className="mb-6">
                            <h3 className="text-lg font-medium mb-3">
                              {phase} Phase
                            </h3>
                            {filteredAndGroupedChecklistItems[
                              phase as keyof typeof filteredAndGroupedChecklistItems
                            ].map((item) => (
                              <div
                                key={item.id}
                                className={`p-4 rounded-lg border ${
                                  item.completed
                                    ? "bg-slate-50 border-slate-200"
                                    : isOverdue(
                                        item?.timeline || "T-0",
                                        !!item.completed
                                      )
                                    ? "bg-red-50 border-red-200"
                                    : "bg-white border-slate-200"
                                } transition-colors mb-3`}
                              >
                                <div className="flex items-start">
                                  {/* Checkbox */}
                                  <div className="pt-0.5">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleChecklistItemToggle(
                                          item.id,
                                          !!item.completed
                                        )
                                      }
                                      className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${
                                        item.completed
                                          ? "bg-primary border-primary text-white"
                                          : "border-slate-300 bg-white"
                                      } ${
                                        updatingItems[item.id]
                                          ? "opacity-50"
                                          : ""
                                      }`}
                                      aria-label={
                                        item.completed
                                          ? "Mark as incomplete"
                                          : "Mark as complete"
                                      }
                                      disabled={
                                        !canUserInteractWithItem(
                                          item.owner || ""
                                        ) || updatingItems[item.id]
                                      }
                                    >
                                      {updatingItems[item.id] ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : item.completed ? (
                                        <Check className="h-3.5 w-3.5" />
                                      ) : null}
                                    </button>
                                  </div>
                                  {/* Content */}
                                  <div className="flex-1">
                                    <div className="flex items-center flex-wrap gap-1.5 mb-1">
                                      <span
                                        className={`text-sm font-medium ml-3 ${
                                          item.completed
                                            ? "text-slate-500 line-through"
                                            : ""
                                        }`}
                                      >
                                        {item.item || "No description"}
                                      </span>
                                      {shouldShowOwnerBadge(
                                        item.owner || ""
                                      ) && (
                                        <Badge
                                          variant="outline"
                                          className={`${getOwnerBadgeColor(
                                            item.owner || ""
                                          )} text-xs py-1 px-3 h-6 font-normal ml-1`}
                                        >
                                          {getOwnerLabel(item.owner || "")}
                                        </Badge>
                                      )}
                                      {isOverdue(
                                        item?.timeline || "T-0",
                                        !!item.completed
                                      ) && (
                                        <Badge
                                          variant="outline"
                                          className="bg-red-100 text-red-800 border-red-200 text-xs py-1 px-3 h-6 font-normal"
                                        >
                                          Overdue
                                        </Badge>
                                      )}
                                    </div>

                                    <div className="flex flex-col gap-2 mt-1">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center text-xs text-muted-foreground">
                                          <Clock className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                                          <span className="font-medium mr-2 whitespace-nowrap">
                                            {item?.timeline || "N/A"}
                                          </span>
                                          {item?.timeline &&
                                            getTimelineDate(item.timeline) && (
                                              <span className="text-slate-500 whitespace-nowrap">
                                                (
                                                {format(
                                                  getTimelineDate(
                                                    item.timeline
                                                  )!,
                                                  "MMM d, yyyy"
                                                )}
                                                )
                                              </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {!item.completed && (
                                            <Badge
                                              className={`${
                                                getDaysMessage(item.timeline)
                                                  .status === "overdue"
                                                  ? "bg-red-100 text-red-800 border-red-200"
                                                  : getDaysMessage(
                                                      item.timeline
                                                    ).status === "today"
                                                  ? "bg-amber-100 text-amber-800 border-amber-200"
                                                  : "bg-green-100 text-green-800 border-green-200"
                                              } text-xs py-0.5 px-2 h-5 font-normal`}
                                            >
                                              {
                                                getDaysMessage(item.timeline)
                                                  .message
                                              }
                                            </Badge>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const type =
                                                item.updateType ||
                                                "EVENT_UPDATE";
                                              setUpdateType(type);
                                              handleUpdateTypeChange(type);
                                              setUpdateContent("");
                                              setShowUpdateForm(true);
                                              document
                                                .getElementById(
                                                  "updates-section"
                                                )
                                                ?.scrollIntoView({
                                                  behavior: "smooth",
                                                });
                                              setTimeout(() => {
                                                contentTextareaRef.current?.focus();
                                              }, 100);
                                            }}
                                          >
                                            <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                            Update
                                          </Button>
                                        </div>
                                      </div>
                                      {item.notes && (
                                        <div className="text-xs text-muted-foreground italic">
                                          {item.notes}
                                        </div>
                                      )}
                                      {item.completed && item.completedAt && (
                                        <div className="flex items-center text-xs text-muted-foreground">
                                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-green-500" />
                                          <span>
                                            Completed{" "}
                                            {format(
                                              new Date(item.completedAt),
                                              "MMM d, yyyy"
                                            )}
                                            {item.completedBy && (
                                              <span className="ml-1">
                                                by {item.completedBy}
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {filteredAndGroupedChecklistItems[
                              phase as keyof typeof filteredAndGroupedChecklistItems
                            ].length === 0 ? (
                              <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed">
                                <CheckSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-muted-foreground">
                                  No matching tasks found
                                </p>
                              </div>
                            ) : null}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Updates Card */}
            <Card id="updates-section" className="border-none shadow-lg">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-xl flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2 text-primary" />
                      Updates & Communications
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Latest updates and communications for this event
                    </CardDescription>
                  </div>
                  {!showUpdateForm && (
                    <Button
                      onClick={() => setShowUpdateForm(true)}
                      className="bg-primary hover:bg-primary/90"
                      size="sm"
                    >
                      <SendHorizonal className="mr-2 h-4 w-4" />
                      New Update
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {showUpdateForm ? (
                  <div className="mb-8 border rounded-lg p-4 bg-slate-50">
                    <h3 className="text-lg font-medium mb-4">
                      Create New Update
                    </h3>
                    <form onSubmit={handleSubmitUpdate}>
                      <div className="grid gap-4">
                        <div className="grid grid-cols-1 gap-2">
                          <Label htmlFor="updateType">Update Type*</Label>
                          <Select
                            value={updateType}
                            onValueChange={handleUpdateTypeChange}
                          >
                            <SelectTrigger id="updateType">
                              <SelectValue placeholder="Select update type" />
                            </SelectTrigger>
                            <SelectContent>
                              {profile?.user_type === "nonprofit" ? (
                                <>
                                  <SelectGroup>
                                    <SelectLabel>
                                      Before the Event (Planning & Coordination)
                                    </SelectLabel>
                                    <SelectItem value="EVENT_UPDATE">
                                      📝 General Update (Planning) – Any prep
                                      updates not covered above.
                                    </SelectItem>
                                    <SelectItem value="EVENT_COLLATERALS">
                                      📦 Event Collaterals – Upload materials to
                                      be used for mobilising volunteers.
                                    </SelectItem>
                                    <SelectItem value="EVENT_LOCATION">
                                      📍 Location Change – Notify about a venue
                                      change.
                                    </SelectItem>
                                    <SelectItem value="EVENT_SHIPMENTS">
                                      📤 Event Materials – Send Pre-event
                                      material dispatches details.
                                    </SelectItem>
                                    <SelectItem value="EVENT_MEET_LINK">
                                      🔗 Meeting Link – Share virtual joining
                                      details.
                                    </SelectItem>
                                    <SelectItem value="EVENT_HOST_DETAILS">
                                      🧑‍💼 Host Information – Announce or confirm
                                      host details.
                                    </SelectItem>
                                  </SelectGroup>
                                  <SelectGroup>
                                    <SelectLabel>
                                      During the Event (Live Execution &
                                      Monitoring)
                                    </SelectLabel>
                                    <SelectItem value="ATTENDANCE">
                                      🧾 Attendance Update – Confirm attendees
                                      or live headcount.
                                    </SelectItem>
                                    <SelectItem value="EVENT_UPDATE">
                                      🔁 General Update (Live) – Share updates
                                      during the event (e.g., delays, progress).
                                    </SelectItem>
                                  </SelectGroup>
                                  <SelectGroup>
                                    <SelectLabel>
                                      After the Event (Wrap-up & Reporting)
                                    </SelectLabel>
                                    <SelectItem value="EVENT_PHOTOS">
                                      📷 Event Photos – Upload snapshots from
                                      the event.
                                    </SelectItem>
                                    <SelectItem value="POST_EVENT_IMPACT">
                                      📊 Impact Report – Share participation,
                                      impact, or outcomes.
                                    </SelectItem>
                                    <SelectItem value="EVENT_SHIPMENTS">
                                      📤 Shipment Information – Share post-event
                                      dispatches or distribution.
                                    </SelectItem>
                                    <SelectItem value="THANKYOU">
                                      🙏 Thank You Message – Auto-send gratitude
                                      notes.
                                    </SelectItem>
                                  </SelectGroup>
                                </>
                              ) : (
                                <>
                                  <SelectGroup>
                                    <SelectLabel>Before the Event</SelectLabel>
                                    <SelectItem value="EVENT_LOCATION">
                                      📍 Location Change – Notify about a venue
                                      change.
                                    </SelectItem>
                                    <SelectItem value="EVENT_UPDATE">
                                      📝 General Update (Planning)
                                    </SelectItem>
                                  </SelectGroup>
                                  <SelectGroup>
                                    <SelectLabel>During the Event</SelectLabel>
                                    <SelectItem value="ATTENDANCE">
                                      🧾 Attendance Update
                                    </SelectItem>
                                  </SelectGroup>
                                  <SelectGroup>
                                    <SelectLabel>After the Event</SelectLabel>
                                    <SelectItem value="THANKYOU">
                                      🙏 Thank You Message
                                    </SelectItem>
                                  </SelectGroup>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                          <Label htmlFor="updateContent">
                            {getContentFieldLabel()}*
                          </Label>
                          <Textarea
                            id="updateContent"
                            ref={contentTextareaRef}
                            placeholder={getContentFieldPlaceholder()}
                            value={updateContent}
                            onChange={(e) => setUpdateContent(e.target.value)}
                            className={
                              updateType === "THANKYOU" ||
                              updateType === "POST_EVENT_IMPACT"
                                ? "min-h-[150px]"
                                : "min-h-[100px]"
                            }
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-2">
                          <Label htmlFor="attachmentUrl">
                            {getAttachmentFieldLabel()}
                          </Label>
                          <div className="flex">
                            <Input
                              id="attachmentUrl"
                              placeholder={
                                isUrlAttachment()
                                  ? "Enter valid URL (https://...)"
                                  : "Enter attachment URL"
                              }
                              value={attachmentUrl}
                              onChange={(e) => setAttachmentUrl(e.target.value)}
                              className="flex-1"
                              type={isUrlAttachment() ? "url" : "text"}
                            />
                            {shouldShowUploadButton() && (
                              <>
                                <input
                                  type="file"
                                  ref={fileInputRef}
                                  onChange={handleFileUpload}
                                  className="hidden"
                                  accept={
                                    updateType === "EVENT_PHOTOS"
                                      ? "image/*"
                                      : updateType === "EVENT_BROCHURES" ||
                                        updateType === "EVENT_COLLATERALS" ||
                                        updateType === "POST_EVENT_IMPACT"
                                      ? ".pdf,.doc,.docx,.ppt,.pptx,.xlsx"
                                      : updateType === "ATTENDANCE"
                                      ? ".csv,.xlsx"
                                      : undefined
                                  }
                                />
                                <Button
                                  variant="outline"
                                  type="button"
                                  className="ml-2"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={isUploading}
                                >
                                  {isUploading ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" /> Upload
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            {isUrlAttachment()
                              ? "Add a direct link that users can click to access the resource"
                              : "Add a link to an attachment such as a document, image, or video"}
                          </p>
                        </div>

                        <div className="space-y-3 mt-2">
                          <h4 className="font-medium text-sm">
                            Email Notifications
                          </h4>
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="sendToBooker">
                                {profile?.user_type === "nonprofit"
                                  ? "Send to Event Booker"
                                  : "Send to Organizer"}
                              </Label>
                              <p className="text-xs text-slate-500">
                                {profile?.user_type === "nonprofit"
                                  ? "Notify the person who booked this event"
                                  : "Notify the nonprofit organization managing this event"}
                              </p>
                            </div>
                            <Switch
                              id="sendToBooker"
                              checked={sendToBooker}
                              onCheckedChange={setSendToBooker}
                              disabled={
                                profile?.user_type === "nonprofit"
                                  ? [
                                      "EVENT_HOST_DETAILS",
                                      "EVENT_COLLATERALS",
                                      "EVENT_SHIPMENTS",
                                      "POST_EVENT_IMPACT",
                                      "ATTENDANCE",
                                    ].includes(updateType)
                                  : ["EVENT_LOCATION"].includes(updateType)
                              }
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="sendToParticipants">
                                Send to All Participants
                              </Label>
                              <p className="text-xs text-slate-500">
                                Notify everyone who is participating in this
                                event
                              </p>
                            </div>
                            <Switch
                              id="sendToParticipants"
                              checked={sendToParticipants}
                              onCheckedChange={setSendToParticipants}
                              disabled={profile?.user_type === "nonprofit"}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end space-x-2 mt-6">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowUpdateForm(false);
                            resetUpdateForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !updateType || !updateContent || isSubmittingUpdate
                          }
                        >
                          {isSubmittingUpdate ? (
                            <>
                              <span className="animate-spin mr-2">⏳</span>
                              Sending...
                            </>
                          ) : (
                            <>
                              <SendHorizonal className="mr-2 h-4 w-4" />
                              Send Update
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : null}

                <div className="space-y-4">
                  {isUpdatesLoading && !isUpdatesError ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  ) : eventUpdates?.data?.length > 0 ||
                    mockUpdates.length > 0 ? (
                    <div className="space-y-6">
                      {(eventUpdates?.data || [])
                        .concat(mockUpdates)
                        .map((update: any) => (
                          <div
                            key={update.id}
                            className={`border rounded-lg p-4 ${getUpdateTypeBgColor(
                              update.update_type
                            )}`}
                          >
                            <div className="flex items-start">
                              <div className="mr-3 mt-0.5">
                                {getUpdateTypeIcon(update.update_type)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm">
                                    {getUpdateTypeLabel(update.update_type)}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {new Date(
                                      update.created_at
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap">
                                  {update.update_content}
                                </p>
                                {update.attachment_url && (
                                  <div className="mt-2">
                                    <a
                                      href={update.attachment_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      <Link className="h-3 w-3 mr-1" />
                                      View Attachment
                                    </a>
                                  </div>
                                )}
                                <div className="mt-2 text-xs text-slate-500">
                                  Posted by{" "}
                                  {profile.id == update.created_by
                                    ? "Me"
                                    : profile.user_type === "nonprofit"
                                    ? "Corporate/Booker"
                                    : "NonProfit"}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                      <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-slate-700">
                        No Updates Yet
                      </h3>
                      <p className="text-sm text-slate-500 mt-1 mb-4">
                        Create the first update for this event to keep everyone
                        informed
                      </p>
                      <Button
                        onClick={() => setShowUpdateForm(true)}
                        variant="outline"
                      >
                        Create an Update
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Bulk Actions Dialog */}
      <Dialog open={bulkActionOpen} onOpenChange={setBulkActionOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-primary">
              <Users className="h-5 w-5 mr-2" />
              Bulk Actions
            </DialogTitle>
            <DialogDescription>
              Update the status of multiple participants at once
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 border border-primary/20 rounded-md bg-primary/10 my-4">
            <h3 className="font-medium mb-2">Select Status</h3>
            <Select defaultValue="signed_up">
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="signed_up">Signed Up</SelectItem>
                <SelectItem value="attended">Attended</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
                <SelectItem value="opt_out">Opt Out</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="p-4 border border-primary/20 rounded-md bg-primary/10 my-4">
            <h3 className="font-medium mb-2">Add Remarks (optional)</h3>
            <Textarea
              value={attendanceRemarks}
              onChange={(e) => setAttendanceRemarks(e.target.value)}
              placeholder="Add any additional remarks about the status update"
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkActionOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => handleBulkStatusUpdate("signed_up")}
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={addParticipantOpen} onOpenChange={setAddParticipantOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Participant</DialogTitle>
            <DialogDescription>
              Add a new participant to this event.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddParticipant}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={newParticipant.name}
                  onChange={(e) =>
                    setNewParticipant({
                      ...newParticipant,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newParticipant.email}
                  onChange={(e) =>
                    setNewParticipant({
                      ...newParticipant,
                      email: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newParticipant.phone}
                  onChange={(e) =>
                    setNewParticipant({
                      ...newParticipant,
                      phone: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddParticipantOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newParticipant.name || !newParticipant.email}
              >
                {addParticipantMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Participant"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Participants</DialogTitle>
            <DialogDescription>
              Upload a CSV file with participant details.
            </DialogDescription>
          </DialogHeader>

          {csvUploadStep === "upload" && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="csv-file">CSV File</Label>
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    ref={csvFileInputRef}
                    onChange={handleCsvFileUpload}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  CSV should have columns: name, email, phone, remarks
                  (optional), status (optional)
                </p>
                <div className="bg-muted p-3 rounded-md mt-2">
                  <p className="text-xs font-mono">Example CSV format:</p>
                  <p className="text-xs font-mono">
                    name,email,phone,remarks,status
                  </p>
                  <p className="text-xs font-mono">
                    John Doe,john@example.com,+1234567890,Vegetarian,signed_up
                  </p>
                  <p className="text-xs font-mono">
                    Jane Smith,jane@example.com,+0987654321,,attended
                  </p>
                </div>
              </div>
            </div>
          )}

          {csvUploadStep === "review" && parsedCsvData.length > 0 && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium">
                    Preview ({parsedCsvData.length} participants)
                  </h3>
                  <Button variant="outline" size="sm" onClick={resetFileInput}>
                    <X className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                </div>
                <div className="border rounded-md max-h-60 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedCsvData.slice(0, 10).map((participant, index) => (
                        <TableRow key={index}>
                          <TableCell>{participant.name}</TableCell>
                          <TableCell>{participant.email}</TableCell>
                          <TableCell>{participant.phone}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`
                                ${
                                  participant.status === "attended"
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : ""
                                }
                                ${
                                  participant.status === "signed_up"
                                    ? "bg-blue-50 text-blue-700 border-blue-200"
                                    : ""
                                }
                                ${
                                  participant.status === "no_show"
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : ""
                                }
                                ${
                                  participant.status === "opt_out"
                                    ? "bg-orange-50 text-orange-700 border-orange-200"
                                    : ""
                                }
                              `}
                            >
                              {participant.status === "signed_up"
                                ? "Signed Up"
                                : participant.status === "no_show"
                                ? "No Show"
                                : participant.status.charAt(0).toUpperCase() +
                                  participant.status.slice(1)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {parsedCsvData.length > 10 && (
                    <div className="p-2 text-center text-sm text-muted-foreground">
                      Showing 10 of {parsedCsvData.length} participants
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {csvUploadStep === "processing" && (
            <div className="py-8 flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin mb-4" />
              <p>Processing {parsedCsvData.length} participants...</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setBulkUploadOpen(false);
                resetFileInput();
              }}
              disabled={csvUploadStep === "processing"}
            >
              Cancel
            </Button>
            {csvUploadStep === "review" && (
              <Button
                type="button"
                onClick={handleBulkUpload}
                disabled={parsedCsvData.length === 0}
              >
                Upload {parsedCsvData.length} Participants
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Action Buttons */}
      {currentStage !== "event_cancelled" &&
        currentStage !== "event_complete" &&
        profile?.user_type === "nonprofit" && (
          <Card className="border shadow-lg my-10 w-full overflow-hidden">
            <CardHeader className="pb-4 pt-5 border-b bg-gradient-to-r from-slate-50 to-gray-50 px-8">
              <CardTitle className="text-xl font-semibold text-slate-800 flex items-center justify-center">
                <BarChart className="h-5 w-5 mr-2 text-primary" />
                Event Controls
              </CardTitle>
              <CardDescription className="text-center">
                Manage this event's lifecycle and status
              </CardDescription>
            </CardHeader>

            <CardContent className="py-8 px-8">
              {/* Event Actions Section */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6 max-w-4xl mx-auto">
                {/* Cancel Event Button - Always show for events that are not completed or cancelled */}
                {![
                  "event_complete",
                  "event_cancelled",
                  "payment_failed",
                ].includes(currentStage) && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400 h-20 w-full sm:w-64 shadow-sm hover:shadow-md transition-all duration-200"
                    onClick={() => setShowCancelDialog(true)}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <XCircle className="h-7 w-7 mb-2" />
                      <span className="font-medium text-base">
                        Cancel Event
                      </span>
                    </div>
                  </Button>
                )}

                {/* Start Event Button - Show for confirmed events */}
                {currentStage === "event_confirmed" && (
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white h-20 w-full sm:w-64 shadow-md hover:shadow-lg transition-all duration-200"
                    onClick={() => setShowStartDialog(true)}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Play className="h-7 w-7 mb-2" />
                      <span className="font-medium text-base">Start Event</span>
                    </div>
                  </Button>
                )}

                {/* Complete Event Button - Show for in-progress events */}
                {currentStage === "event_in_progress" && (
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white h-20 w-full sm:w-64 shadow-md hover:shadow-lg transition-all duration-200"
                    onClick={() => setShowCompleteDialog(true)}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <CheckCircle2 className="h-7 w-7 mb-2" />
                      <span className="font-medium text-base">
                        Mark as Completed
                      </span>
                    </div>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
